// The floating assistant panel (spec §3.8): right side, 420 px, floating by default, docked when pinned at ≥1280 px
// (the main column shrinks, CSS), fullscreen below 768 px. A complementary region with a heading; below 768 px (phones,
// or desktop zoom at 200 %+) it covers the page, so it becomes a modal dialog and the rest of the shell is inert. Esc
// closes. The conversation follows the page: a project page talks in that project's session, any other in the Home one.
// D36: the panel does not exist on Home; elsewhere it is open (floating or docked/pinned) or collapsed to a slim rail
// button on the right edge, and that choice is remembered (AssistantProvider).
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useI18n } from "../../i18n/index.tsx";
import { IconButton } from "../../components/Button.tsx";
import { PanelRightIcon, SparklesIcon, XIcon } from "../../components/icons.tsx";
import { AssistantConversation, projectIdOf } from "./AssistantConversation.tsx";
import { useAssistant } from "./AssistantProvider.tsx";

const FULLSCREEN = "(max-width: 767px)";

function useFullscreen(): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(FULLSCREEN).matches);
  useEffect(() => {
    const mq = window.matchMedia(FULLSCREEN);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return matches;
}

export function AssistantPanel() {
  const { t } = useI18n();
  const { isOpen, close, pinned, setPinned, request, clearRequest, available, openSeq } = useAssistant();
  const { pathname } = useLocation();
  const ref = useRef<HTMLElement>(null);
  const seenSeq = useRef(openSeq);
  // A panel that appears without a fresh open (restored from the last visit) gives back the focus its conversation
  // took from <body> on mount. Layout effects run before the conversation's mount effect, passive ones after it; this
  // effect is declared before the one below so a fresh open is still seen as fresh.
  const focusBefore = useRef<Element | null>(null);
  useLayoutEffect(() => {
    focusBefore.current = document.activeElement;
  }, [isOpen, available]);
  useEffect(() => {
    if (!isOpen || !available || openSeq !== seenSeq.current) return;
    const before = focusBefore.current;
    const now = document.activeElement;
    if ((before === null || before === document.body) && now instanceof HTMLElement && ref.current?.contains(now)) now.blur();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, available]);
  // Focus goes to the composer when the panel is opened — not when the conversation follows a navigation, nor when
  // the panel comes back open from a previous visit (a restored panel never steals focus from the page).
  useEffect(() => {
    if (openSeq === seenSeq.current) return;
    seenSeq.current = openSeq;
    if (isOpen) ref.current?.querySelector("textarea")?.focus();
  }, [isOpen, openSeq]);

  // Fullscreen = modal: every sibling in the shell (skip link, sidebar, drawer, main column) leaves the tab order and
  // the accessibility tree while the panel covers it.
  const modal = useFullscreen() && isOpen && available;
  useEffect(() => {
    const siblings = modal && ref.current?.parentElement ? [...ref.current.parentElement.children].filter((el): el is HTMLElement => el instanceof HTMLElement && el !== ref.current && !el.inert) : [];
    for (const el of siblings) el.inert = true;
    return () => {
      for (const el of siblings) el.inert = false;
    };
  }, [modal]);

  if (!available || !isOpen) return null;
  const projectId = projectIdOf(pathname);
  return (
    <aside
      ref={ref}
      id="assistant-panel"
      className={`assistant-panel${pinned ? " assistant-panel--docked" : ""}`}
      role={modal ? "dialog" : undefined}
      aria-modal={modal || undefined}
      aria-labelledby="assistant-title"
      onKeyDown={(e) => {
        if (e.key === "Escape" && !e.defaultPrevented) {
          e.preventDefault();
          close();
        }
      }}
    >
      <div className="assistant-head">
        <SparklesIcon className="assistant-head-icon" />
        <div className="assistant-head-text">
          <h2 id="assistant-title">{t("assistant.title")}</h2>
          <p className="assistant-scope">{t(projectId ? "assistant.scope.project" : "assistant.scope.home")}</p>
        </div>
        <IconButton label={t("assistant.dock")} className="assistant-dock" aria-pressed={pinned} onClick={() => setPinned(!pinned)}>
          <PanelRightIcon />
        </IconButton>
        <IconButton label={t("assistant.close")} onClick={close}>
          <XIcon />
        </IconButton>
      </div>
      <AssistantConversation key={projectId ?? "home"} projectId={projectId} request={request} onRequestHandled={clearRequest} />
    </aside>
  );
}

/** Collapsed state: a slim button on the right edge reopens the panel (hidden on phones, where the topbar has one). */
export function AssistantRail() {
  const { t } = useI18n();
  const { isOpen, open, available } = useAssistant();
  if (!available || isOpen) return null;
  return (
    <button type="button" className="assistant-rail" aria-label={t("assistant.reopen")} title={t("assistant.reopen")} aria-keyshortcuts="Control+J Meta+J" onClick={() => open()}>
      <SparklesIcon size={18} />
    </button>
  );
}

/** Topbar opener (also Ctrl/⌘+J). Not rendered on Home, where the page itself is the conversation. */
export function AssistantButton() {
  const { t } = useI18n();
  const { isOpen, toggle, available } = useAssistant();
  if (!available) return null;
  return (
    <IconButton
      label={t("assistant.title")}
      className="topbar-assistant"
      aria-expanded={isOpen}
      aria-controls={isOpen ? "assistant-panel" : undefined}
      aria-keyshortcuts="Control+J Meta+J"
      onClick={toggle}
    >
      <SparklesIcon />
    </IconButton>
  );
}
